/* oxlint-disable react/only-export-components -- archivo de configuración de router: exporta `router` (no un componente) junto a los guards internos, no está pensado para Fast Refresh */
import { lazy, Suspense, type ReactElement } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { AppLayout }       from '../pages/AppLayout'

const LoginPage = lazy(() => import('../pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })))
const RecuperarPage = lazy(() => import('../pages/auth/RecuperarPage').then((m) => ({ default: m.RecuperarPage })))
const MantenimientoPage = lazy(() => import('../pages/MantenimientoPage').then((m) => ({ default: m.MantenimientoPage })))
const DashboardPage = lazy(() => import('../pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })))
const ClientesPage = lazy(() => import('../pages/clientes/ClientesPage').then((m) => ({ default: m.ClientesPage })))
const ClienteDetallePage = lazy(() => import('../pages/clientes/ClienteDetallePage').then((m) => ({ default: m.ClienteDetallePage })))
const ProductosPage = lazy(() => import('../pages/productos/ProductosPage').then((m) => ({ default: m.ProductosPage })))
const ProductoDetallePage = lazy(() => import('../pages/productos/ProductoDetallePage').then((m) => ({ default: m.ProductoDetallePage })))
const FabricasPage = lazy(() => import('../pages/fabricas/FabricasPage').then((m) => ({ default: m.FabricasPage })))
const CertificadoresPage = lazy(() => import('../pages/certificadores/CertificadoresPage').then((m) => ({ default: m.CertificadoresPage })))
const ProyectosPage = lazy(() => import('../pages/proyectos/ProyectosPage').then((m) => ({ default: m.ProyectosPage })))
const ProyectoDetallePage = lazy(() => import('../pages/proyectos/ProyectoDetallePage').then((m) => ({ default: m.ProyectoDetallePage })))
const BibliotecaPage = lazy(() => import('../pages/biblioteca/BibliotecaPage').then((m) => ({ default: m.BibliotecaPage })))
const LotesPage = lazy(() => import('../pages/lotes/LotesPage').then((m) => ({ default: m.LotesPage })))
const LoteDetallePage = lazy(() => import('../pages/lotes/LoteDetallePage').then((m) => ({ default: m.LoteDetallePage })))
const PortalClientePage = lazy(() => import('../pages/portal-cliente/PortalClientePage').then((m) => ({ default: m.PortalClientePage })))
const VerificarCertificadoPage = lazy(() => import('../pages/public/VerificarCertificadoPage').then((m) => ({ default: m.VerificarCertificadoPage })))
const OpcionesGeneralesPage = lazy(() => import('../pages/admin/AdminPages').then((m) => ({ default: m.OpcionesGeneralesPage })))

function RouteFallback() {
  return (
    <div className="px-8 py-7 text-sm text-text-muted">
      Cargando...
    </div>
  )
}

function lazyRoute(element: ReactElement) {
  return (
    <Suspense fallback={<RouteFallback />}>
      {element}
    </Suspense>
  )
}

// ─── Guard: requiere autenticación ────────────────────────────────────────────
function RequireAuth() {
  const { isAuthenticated } = useAuth()
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  return <Outlet />
}

function PortalHomeRedirect() {
  const { isAuthenticated } = useAuth()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <Navigate to="/dashboard" replace />
}

// ─── Guard: solo rol 'conformity' ────────────────────────────────────────────
// La autorización real también se valida en middleware y servicios del backend.
// Este guard es defensa en profundidad del lado del cliente.
function RequireConformity() {
  const { isConformity, isAuthenticated } = useAuth()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!isConformity)    return <Navigate to="/dashboard" replace />
  return <Outlet />
}

// ─── Guard: solo rol 'cliente' ────────────────────────────────────────────────
function RequireCliente() {
  const { isConformity, isAuthenticated } = useAuth()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (isConformity)     return <Navigate to="/dashboard" replace />
  return <Outlet />
}

export const router = createBrowserRouter([
  // Rutas públicas
  { path: '/login',        element: lazyRoute(<LoginPage />) },
  { path: '/recuperar',    element: lazyRoute(<RecuperarPage />) },
  { path: '/mantenimiento', element: lazyRoute(<MantenimientoPage />) },
  { path: '/verificar/certificado/:id', element: lazyRoute(<VerificarCertificadoPage />) },

  // Rutas privadas — requieren auth
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          // Redirección raíz
          { index: true, element: <PortalHomeRedirect /> },

          // Módulos compartidos (con filtro de tenant en el backend)
          { path: 'productos',     element: lazyRoute(<ProductosPage />) },
          { path: 'productos/:id', element: lazyRoute(<ProductoDetallePage />) },
          { path: 'proyectos',     element: lazyRoute(<ProyectosPage />) },
          { path: 'proyectos/:id', element: lazyRoute(<ProyectoDetallePage />) },
          { path: 'lotes',         element: lazyRoute(<LotesPage />) },
          { path: 'lotes/:id',     element: lazyRoute(<LoteDetallePage />) },
          { path: 'biblioteca',    element: lazyRoute(<BibliotecaPage />) },
          { path: 'dashboard',     element: lazyRoute(<DashboardPage />) },
          { path: 'opciones-generales', element: lazyRoute(<OpcionesGeneralesPage />) },
          { path: 'mi-cuenta', element: <Navigate to="/opciones-generales?seccion=cuenta" replace /> },

          // Solo Conformity
          {
            element: <RequireConformity />,
            children: [
              { path: 'clientes',     element: lazyRoute(<ClientesPage />) },
              { path: 'clientes/:id', element: lazyRoute(<ClienteDetallePage />) },
              { path: 'fabricas',      element: lazyRoute(<FabricasPage />) },
              { path: 'certificadores', element: lazyRoute(<CertificadoresPage />) },
            ],
          },

          // Solo Cliente
          {
            element: <RequireCliente />,
            children: [
              { path: 'portal-cliente', element: lazyRoute(<PortalClientePage />) },
            ],
          },
        ],
      },
    ],
  },

  // Fallback
  { path: '*', element: <PortalHomeRedirect /> },
])
